import React, { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Modal, Spinner } from "react-bootstrap";
import {
  FiPlus,
  FiChevronDown,
  FiChevronRight,
  FiEdit2,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import moment from "moment";
import {
  listEndpoints,
  deleteEndpoint,
} from "../../../services/integration.service";
import { ToastrContext } from "../../../provider/toastrContext";
import CreateEndpointModal from "./createEndpointModal";
import EditEndpointModal from "./editEndpointModal";
import DeliveriesPanel from "./deliveriesPanel";

const PAGE_LIMIT = 20;

const formatTs = (ts) => (ts ? moment(ts).format("DD-MM-YYYY HH:mm") : "—");

const ENDPOINT_STATUS_BADGE = {
  enabled: "bg-success",
  disabled: "bg-secondary",
  auto_disabled: "bg-warning",
};

const EndpointStatusBadge = ({ status }) => (
  <span
    className={`badge ${ENDPOINT_STATUS_BADGE[status] || "bg-secondary"}`}
  >
    {status}
  </span>
);

const EventsCell = ({ events }) => {
  if (!events || events.length === 0)
    return <span className="text-muted">—</span>;
  const head = events.slice(0, 2);
  const rest = events.length - head.length;
  const tooltip = events.join("\n");
  return (
    <span title={tooltip}>
      {head.map((e) => (
        <span key={e} className="badge bg-secondary me-1">
          {e}
        </span>
      ))}
      {rest > 0 && (
        <span className="badge bg-light text-dark">+{rest} more</span>
      )}
    </span>
  );
};

const URLCell = ({ url }) => (
  <span
    className="text-truncate d-inline-block"
    style={{ maxWidth: 320, verticalAlign: "middle" }}
    title={url}
  >
    {url}
  </span>
);

const WebhooksTab = ({ appPubID }) => {
  const { showSuccess, showError } = useContext(ToastrContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const eventIDFilter = searchParams.get("event_id") || "";
  const [endpoints, setEndpoints] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // When the cross-tab "Find deliveries" link sets ?event_id=…, auto-expand
  // the first endpoint so the filtered deliveries panel surfaces without an
  // extra click. Only runs when the filter changes (or endpoints first load).
  useEffect(() => {
    if (eventIDFilter && endpoints.length > 0) {
      setExpanded((cur) => cur || endpoints[0].id);
    }
  }, [eventIDFilter, endpoints]);

  const clearEventFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("event_id");
    setSearchParams(next, { replace: true });
  };

  const load = async (after = null) => {
    setLoading(true);
    try {
      const params = { limit: PAGE_LIMIT };
      if (after) params.starting_after = after;
      const res = await listEndpoints(appPubID, params);
      const items = Array.isArray(res.data) ? res.data : [];
      setEndpoints((prev) => (after ? [...prev, ...items] : items));
      setHasMore(!!res.has_more);
      setCursor(res.next_cursor || null);
    } catch (e) {
      showError((e && e.message) || "Failed to load endpoints");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setEndpoints([]);
    setCursor(null);
    setHasMore(false);
    setExpanded(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appPubID]);

  const refreshAll = () => {
    setEndpoints([]);
    setCursor(null);
    setHasMore(false);
    load();
  };

  const handleCreated = () => {
    refreshAll();
  };

  const handleSaved = (updated) => {
    if (!updated) {
      refreshAll();
      return;
    }
    setEndpoints((prev) =>
      prev.map((e) => (e.id === updated.id ? { ...e, ...updated } : e))
    );
  };

  const confirmAndDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await deleteEndpoint(confirmDelete.id);
      showSuccess("Endpoint deleted");
      if (expanded === confirmDelete.id) setExpanded(null);
      setConfirmDelete(null);
      refreshAll();
    } catch (e) {
      showError((e && e.message) || "Failed to delete endpoint");
    } finally {
      setDeleting(false);
    }
  };

  const toggleExpand = (id) => {
    setExpanded((cur) => (cur === id ? null : id));
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-0">Webhook Endpoints</h5>
          <p className="text-muted mb-0 small">
            URLs that receive HMAC-signed event deliveries. Click a row to see
            its delivery history; use Replay to resend a past delivery.
          </p>
        </div>
        <Button variant="primary" onClick={() => setShowCreate(true)}>
          <FiPlus className="align-middle me-1" size={16} />
          Add Endpoint
        </Button>
      </div>

      {eventIDFilter && (
        <div className="alert bg-info-subtle d-flex justify-content-between align-items-center py-2 mb-3">
          <div>
            Showing deliveries for <code>{eventIDFilter}</code>
          </div>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={clearEventFilter}
            title="Clear event filter"
          >
            <FiX className="align-middle me-1" />
            Clear
          </Button>
        </div>
      )}

      <div className="table-responsive">
        {loading && endpoints.length === 0 ? (
          <div className="text-center p-5">
            <Spinner animation="border" />
          </div>
        ) : endpoints.length === 0 ? (
          <div className="text-center p-5 text-muted">
            <p className="mb-0">No webhook endpoints yet.</p>
          </div>
        ) : (
          <table className="table table-striped align-middle">
            <thead>
              <tr>
                <th style={{ width: 30 }}></th>
                <th>URL</th>
                <th>Status</th>
                <th>Environment</th>
                <th>Subscribed Events</th>
                <th>Failures</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {endpoints.map((ep) => {
                const isOpen = expanded === ep.id;
                return (
                  <React.Fragment key={ep.id}>
                    <tr>
                      <td>
                        <button
                          type="button"
                          className="btn btn-link p-0 text-muted"
                          onClick={() => toggleExpand(ep.id)}
                          title={
                            isOpen
                              ? "Hide deliveries"
                              : "Show deliveries"
                          }
                        >
                          {isOpen ? <FiChevronDown /> : <FiChevronRight />}
                        </button>
                      </td>
                      <td>
                        <URLCell url={ep.url} />
                      </td>
                      <td>
                        <EndpointStatusBadge status={ep.status} />
                      </td>
                      <td>
                        <span
                          className={`badge bg-${
                            ep.environment === "live" ? "primary" : "info"
                          }`}
                        >
                          {ep.environment}
                        </span>
                      </td>
                      <td>
                        <EventsCell events={ep.subscribed_events} />
                      </td>
                      <td>{ep.consecutive_failures}</td>
                      <td>{formatTs(ep.ts_created)}</td>
                      <td>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="me-1"
                          onClick={() => setEditTarget(ep)}
                          title="Edit endpoint"
                        >
                          <FiEdit2 />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => setConfirmDelete(ep)}
                          title="Delete endpoint"
                        >
                          <FiTrash2 />
                        </Button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={8} className="p-0">
                          <DeliveriesPanel
                            endpointID={ep.id}
                            eventIDFilter={eventIDFilter || undefined}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
        {hasMore && (
          <div className="text-center">
            <Button
              variant="outline-secondary"
              onClick={() => load(cursor)}
              disabled={loading}
            >
              {loading ? "Loading..." : "Load more"}
            </Button>
          </div>
        )}
      </div>

      <CreateEndpointModal
        show={showCreate}
        onClose={() => setShowCreate(false)}
        appPubID={appPubID}
        onCreated={handleCreated}
      />

      <EditEndpointModal
        show={!!editTarget}
        onClose={() => setEditTarget(null)}
        endpoint={editTarget}
        onSaved={handleSaved}
      />

      <Modal
        show={!!confirmDelete}
        onHide={() => !deleting && setConfirmDelete(null)}
        centered
        size="sm"
        backdrop="static"
      >
        <Modal.Header className="bg-danger-subtle" closeButton={!deleting}>
          <Modal.Title>Delete endpoint</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            This will permanently delete the endpoint below. No further events
            will be delivered to it. Past deliveries are retained.
          </p>
          {confirmDelete && (
            <p className="mb-0">
              <code style={{ wordBreak: "break-all" }}>
                {confirmDelete.url}
              </code>
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setConfirmDelete(null)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmAndDelete}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default WebhooksTab;
